import { useState } from "react";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Send, ArrowRight } from "lucide-react";
import { ContainerScrollAnimation } from "./ui/ContainerScrollAnimation";

const initialForm = { name: "", email: "", subject: "", message: "" };

function ContactForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "El nombre es obligatorio";
    if (!form.email.trim()) {
      next.email = "El email es obligatorio";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = "Ingresá un email válido";
    }
    if (!form.subject.trim()) next.subject = "El asunto es obligatorio";
    if (!form.message.trim()) next.message = "El mensaje es obligatorio";
    return next;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validation = validate();
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    console.log("Form submitted:", form);
    setSubmitted(true);
    setForm(initialForm);
    setErrors({});
  };

  return (
    <section id="contacto" className="bg-soft-gray overflow-hidden">
      <ContainerScrollAnimation
        titleComponent={
          <>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-primary tracking-tight">
              ¿Listo para tu próximo proyecto?
            </h2>
            <p className="mt-4 text-base md:text-lg text-gray-500 max-w-xl mx-auto leading-relaxed">
              Contanos tu idea y transformémosla en algo extraordinario. Sin
              compromisos, solo una conversación.
            </p>
          </>
        }
      >
        <div className="h-full w-full flex items-start md:items-center justify-center p-3 sm:p-4 md:p-12 overflow-hidden">
          <div className="w-full max-w-lg md:max-w-2xl">
            <div className="text-center mb-4 md:mb-6">
              <h3 className="text-lg md:text-4xl font-bold text-primary">
                Contacto
              </h3>
              <p className="mt-1 md:mt-3 text-xs md:text-lg text-gray-500">
                Completá el formulario o escribinos directo.
              </p>
              <div className="mt-3 md:mt-6 flex flex-col sm:flex-row items-center justify-center gap-2 md:gap-4">
                <Button className="bg-primary hover:bg-accent text-xs md:text-base h-9 px-4 md:h-11 md:px-6">
                  Hablemos por WhatsApp
                  <ArrowRight className="ml-2 h-3 w-3 md:h-4 md:w-4" />
                </Button>
                <Button variant="outline" className="text-xs md:text-base h-9 px-4 md:h-11 md:px-6">
                  Enviar un email
                </Button>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-3 md:my-6" />

            {submitted ? (
              <div className="text-center py-8 md:py-16">
                <p className="text-xl md:text-2xl font-semibold text-primary">
                  ¡Mensaje enviado!
                </p>
                <p className="mt-2 text-gray-500 md:text-lg">
                  Gracias por contactarnos. Te responderemos pronto.
                </p>
                <Button
                  variant="outline"
                  className="mt-6 md:text-base md:h-11 md:px-6"
                  onClick={() => setSubmitted(false)}
                >
                  Enviar otro mensaje
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-2 md:space-y-5" noValidate>
                <div>
                  <Input
                    name="name"
                    placeholder="Nombre"
                    value={form.name}
                    onChange={handleChange}
                    className={`md:h-12 md:text-base ${errors.name ? "border-red-400" : ""}`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={handleChange}
                    className={`md:h-12 md:text-base ${errors.email ? "border-red-400" : ""}`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                  )}
                </div>

                <div>
                  <Input
                    name="subject"
                    placeholder="Asunto"
                    value={form.subject}
                    onChange={handleChange}
                    className={`md:h-12 md:text-base ${errors.subject ? "border-red-400" : ""}`}
                  />
                  {errors.subject && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.subject}
                    </p>
                  )}
                </div>

                <div>
                  <textarea
                    name="message"
                    placeholder="Contanos sobre tu proyecto"
                    rows={3}
                    value={form.message}
                    onChange={handleChange}
                    className={`flex w-full border bg-white px-3 py-2 text-sm md:text-base md:px-4 md:py-3 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none rounded-md ${
                      errors.message ? "border-red-400" : "border-gray-200"
                    }`}
                  />
                  {errors.message && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.message}
                    </p>
                  )}
                </div>

                <Button className="w-full h-10 md:h-14 text-sm md:text-lg bg-primary hover:bg-accent">
                  Enviar mensaje
                  <Send className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </ContainerScrollAnimation>
    </section>
  );
}

export default ContactForm;
